import React from 'react';
import { Button } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import TableToolbar from '../../components/table/TableToolbar';
import Table from '../../components/table/Table';
import Device from '../../models/device';
import PermissionRow from './permissionRow';
import TableHead, { Order } from '../../components/table/TableHead';
import { filterDevices, getComparator } from '../../utils';
import { permissionFieldHeaders, permissionTableFields } from './permissionFieldsConfig';
import { User } from '../../models/user';
import UserFetch from '../../userFetch';
import BulkAssignDialog from './BulkAssignDialog';

interface PermissionsTableProps {
  devices: Device[];
}

function PermissionsTable(props: PermissionsTableProps) {

  const [order, setOrder] = React.useState<Order>(() => {
    const savedOrder = sessionStorage.getItem('permissionTableOrder');
    return (savedOrder as Order) || 'asc';
  });

  const [orderBy, setOrderBy] = React.useState<keyof Device>(() => {
    const savedOrderBy = sessionStorage.getItem('permissionTableOrderBy');
    return (savedOrderBy as keyof Device) || 'hostname';
  });

  const [selected, setSelected] = React.useState<string[]>([]);
  const [filter, setFilter] = React.useState<string | undefined>(() => {
    // Load the filter from sessionStorage if it exists
    const savedFilter = sessionStorage.getItem('permissionTableFilter');
    return savedFilter || undefined;
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);

  const [users, setUsers] = React.useState([] as User[]);
  const [bulkAssignOpen, setBulkAssignOpen] = React.useState(false);

  React.useEffect(() => {
    UserFetch('/api/users')
      .then((response) => response.json())
      .then((d: User[]) => {
        setUsers(d);
      });
  }, []);

  const handleBulkAssignComplete = () => {
    setSelected([]);
  };

  const filteredRows = React.useMemo(
    () => filterDevices(props.devices, filter),
    [props.devices, filter]
  );

  const visibleRows = React.useMemo(
    () => filteredRows
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredRows],
  );

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof Device, ctrlKey = false) => {
    const isAsc = orderBy === property && order === 'asc';

    const defaultDescendingFields = [] as string[];
    const isSameColumn = orderBy === property;

    let newOrder: Order;

    if (ctrlKey) {
      if (isSameColumn) {
        newOrder = isAsc ? 'asc' : 'desc';
      } else {
        newOrder = defaultDescendingFields.includes(property as string) ? 'asc' : 'desc';
      }
    } else {
      if (isSameColumn) {
        newOrder = isAsc ? 'desc' : 'asc';
      } else {
        newOrder = defaultDescendingFields.includes(property as string) ? 'desc' : 'asc';
      }
    }

    setOrder(newOrder);
    setOrderBy(property);

    sessionStorage.setItem('permissionTableOrder', newOrder);
    sessionStorage.setItem('permissionTableOrderBy', property as string);
  };

  const handleRowClick = (serial_number: string) => {
    const selectedIndex = selected.indexOf(serial_number);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, serial_number);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredRows.map((n) => n.serial_number);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeFilter = (filter?: string) => {
    const lowercaseFilter = filter?.toLowerCase();
    setFilter(lowercaseFilter);

    if (lowercaseFilter) {
      sessionStorage.setItem('permissionTableFilter', lowercaseFilter);
    } else {
      sessionStorage.removeItem('permissionTableFilter');
    }
  };

  const handleExpandAll = React.useCallback(() => {
    visibleRows.forEach((device) => {
      sessionStorage.setItem(`perm_drawer_${device.serial_number}`, 'true');
    });
    window.location.reload();
  }, [visibleRows]);

  const handleCollapseAll = React.useCallback(() => {
    visibleRows.forEach((device) => {
      sessionStorage.setItem(`perm_drawer_${device.serial_number}`, 'false');
    });
    window.location.reload();
  }, [visibleRows]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.code === 'KeyX') {
        event.preventDefault();
        const anyExpanded = visibleRows.some((device) =>
          sessionStorage.getItem(`perm_drawer_${device.serial_number}`) === 'true'
        );
        if (anyExpanded) {
          handleCollapseAll();
        } else {
          handleExpandAll();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visibleRows, handleExpandAll, handleCollapseAll]);

  return <Table
    rowsNumber={filteredRows.length}
    page={page}
    rowsPerPage={rowsPerPage}
    handleChangePage={handleChangePage}
    handleChangeRowsPerPage={handleChangeRowsPerPage}
    paginationLabel={filteredRows.length === props.devices.length
      ? `Total: ${props.devices.length}`
      : `Showing ${filteredRows.length} of ${props.devices.length}`}
    toolbar={
      <TableToolbar
        title={'Permissions'}
        numSelected={selected.length}
        autoRefreshActive={false}
        filterValue={filter}
        onRequestFilter={handleChangeFilter}
        selectActionBar={
          <Button
            variant="contained"
            size="small"
            startIcon={<SecurityIcon />}
            onClick={() => setBulkAssignOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Assign Permissions
          </Button>
        }
      />
    }
    head={<TableHead
      numSelected={selected.length}
      order={order}
      orderBy={orderBy}
      onSelectAllClick={handleSelectAllClick}
      onRequestSort={handleRequestSort}
      onExpandAll={handleExpandAll}
      onCollapseAll={handleCollapseAll}
      rowCount={filteredRows.length}
      tableFields={permissionTableFields}
      fieldHeaders={permissionFieldHeaders}
    />}
  >
    {visibleRows.map((device, index) => {
      const isItemSelected = selected.indexOf(device.serial_number) !== -1;
      const labelId = `enhanced-table-checkbox-${index}`;

      return (
        <PermissionRow
          users={users}
          key={device.serial_number}
          device={device}
          rowClicked={handleRowClick}
          selected={isItemSelected}
          labelId={labelId} />
      );
    })}
    <BulkAssignDialog
      open={bulkAssignOpen}
      users={users}
      selectedDevices={selected}
      onClose={() => setBulkAssignOpen(false)}
      onComplete={handleBulkAssignComplete}
    />
  </Table>;
}

export default PermissionsTable;